import { useEffect, useMemo, useRef } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useDebounce } from './useDebounce'

export type InfiniteLookupPage<T> = {
  items: T[]
  hasNextPage: boolean
  totalCount?: number
}

type UseInfiniteLookupOptions<T> = {
  /** Base query key (search term is appended automatically). */
  queryKey: readonly unknown[]
  enabled?: boolean
  pageSize?: number
  searchTerm: string
  /** Configured dropdown search field keys — sent to the API for DB search. */
  searchFields?: string[]
  debounceMs?: number
  getId: (item: T) => string
  fetchPage: (args: {
    page: number
    pageSize: number
    search: string
    searchFields: string[]
  }) => Promise<InfiniteLookupPage<T>>
  /** Scroll container for infinite loading (dropdown list). Falls back to viewport. */
  scrollRootRef?: React.RefObject<HTMLElement | null>
}

/**
 * Server-side search + infinite scroll for large dropdowns / lookups.
 * Never loads the full table — only pageSize rows per request.
 * Search always goes to the API (database), not local cache filtering.
 */
export function useInfiniteLookup<T>({
  queryKey,
  enabled = true,
  pageSize = 40,
  searchTerm,
  searchFields = [],
  debounceMs = 250,
  getId,
  fetchPage,
  scrollRootRef,
}: UseInfiniteLookupOptions<T>) {
  const debouncedSearch = useDebounce(searchTerm.trim(), debounceMs)
  const searchFieldsKey = useMemo(
    () => [...searchFields].map((f) => f.trim()).filter(Boolean).sort().join(','),
    [searchFields]
  )
  const resolvedSearchFields = useMemo(
    () => (searchFieldsKey ? searchFieldsKey.split(',') : []),
    [searchFieldsKey]
  )
  const sentinelRef = useRef<HTMLDivElement>(null)

  const query = useInfiniteQuery({
    queryKey: [...queryKey, debouncedSearch, searchFieldsKey, pageSize],
    enabled,
    initialPageParam: 1,
    staleTime: 30_000,
    queryFn: async ({ pageParam }) =>
      fetchPage({
        page: pageParam as number,
        pageSize,
        search: debouncedSearch,
        searchFields: resolvedSearchFields,
      }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasNextPage ? allPages.length + 1 : undefined,
  })

  const items = useMemo(() => {
    const map = new Map<string, T>()
    for (const page of query.data?.pages ?? []) {
      for (const item of page.items) {
        const id = getId(item)
        if (id && !map.has(id)) map.set(id, item)
      }
    }
    return Array.from(map.values())
  }, [query.data, getId])

  useEffect(() => {
    if (!enabled || !query.hasNextPage || query.isFetchingNextPage) return

    const sentinel = sentinelRef.current
    if (!sentinel) return

    const root = scrollRootRef?.current ?? null
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
          void query.fetchNextPage()
        }
      },
      { root, rootMargin: '100px', threshold: 0.01 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [
    enabled,
    query.hasNextPage,
    query.isFetchingNextPage,
    query.fetchNextPage,
    items.length,
    scrollRootRef,
  ])

  return {
    items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    hasNextPage: !!query.hasNextPage,
    refetch: query.refetch,
    sentinelRef,
    debouncedSearch,
  }
}

/** Normalize common API list envelopes into InfiniteLookupPage. */
export function mapPagedResponse<T>(
  payload: any,
  mapItem: (raw: any) => T | null,
  page: number,
  pageSize: number
): InfiniteLookupPage<T> {
  const rawList: any[] = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.items)
        ? payload.items
        : []

  const items = rawList.map(mapItem).filter((x): x is T => x != null)

  const pagination = payload?.pagination ?? {}
  const totalPages =
    Number(pagination.totalPages ?? payload?.totalPages ?? payload?.totalPage ?? 0) || 0
  const hasNextExplicit = pagination.hasNextPage ?? payload?.hasNextPage ?? payload?.hasNext

  const hasNextPage =
    typeof hasNextExplicit === 'boolean'
      ? hasNextExplicit
      : totalPages > 0
        ? page < totalPages
        : items.length >= pageSize

  return {
    items,
    hasNextPage,
    totalCount: Number(pagination.totalCount ?? payload?.totalCount ?? 0) || undefined,
  }
}

/** Build API params for server-side dropdown search. */
export function buildDropdownSearchParams(search: string, searchFields: string[]) {
  return {
    searchTerm: search || undefined,
    searchFields: searchFields.length > 0 ? searchFields.join(',') : undefined,
  }
}
